import React, { useState, useRef, useEffect } from "react";

import "./cfdPropertyFilters.css";
import {
  BiFilter,
  BiHome,
  BiRotateRight,
  BiSolidDownArrow,
  BiSolidUpArrow,
  BiSort,
} from "react-icons/bi";
import { trackButtonClick } from "@/utils/analytics";

const CfdPropertyFilters = ({ filters, onFilterChange, activeTab }) => {
  const [activeFilter, setActiveFilter] = useState(null);
  const filterRef = useRef(null);
  const propertyTypes = [
    {
      label: "Studio",
      value: "SU",
    },
    {
      label: "Single Family",
      value: "SF",
    },
    {
      label: "Multi Family",
      value: "MF",
    },
    {
      label: "Condo/Apartment",
      value: "CO",
    },
    {
      label: "Mobile Home",
      value: "MH",
    },
    {
      label: "Mixed Use",
      value: "MU",
    },
    {
      label: "Land",
      value: "VL",
    },
    {
      label: "Prefabricated",
      value: "PF",
    },
    {
      label: "Townhome",
      value: "TH",
    },
    {
      label: "Co-op",
      value: "CP",
    },
    {
      label: "Other",
      value: "OT",
    },
  ];
  const occupancyStatuses = [
    {
      label: "Occupied - No Interior Access",
      value: "Y",
    },
    {
      label: "Vacacnt - Interior Access",
      value: "N",
    },
  ];
  const sortBy = [
    { label: "New Properties", value: "newest" },
    { label: "List Price", value: "listPrice" },
    { label: "Offers Placed", value: "placedOn" },
    { label: "Address", value: "address" },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setActiveFilter(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCheckboxChange = (filterType, value) => {
    const currentFilters = [...filters[filterType]];
    const index = currentFilters.indexOf(value);

    if (index === -1) {
      currentFilters.push(value);
    } else {
      currentFilters.splice(index, 1);
    }

    onFilterChange(filterType, currentFilters);
  };

  const toggleFilter = (filterName) => {
    setActiveFilter(activeFilter === filterName ? null : filterName);
  };

  return (
    <div className="cfd-filter-row" ref={filterRef}>
      {/* Property Type Filter */}

      {activeTab !== "history" && (
        <>
          <div
            className="cfd-filter-box"
            onMouseLeave={() => setActiveFilter(null)}
            onMouseEnter={() => toggleFilter("propertyType")}
          >
            <div className="cfd-filter-header">
              <div className="cfd-filter-header-content">
                {/* <BiFilter className="cfd-filter-icon" /> */}
                <span>Property Type</span>
              </div>

              <span className="mx-1">
                {activeFilter === "propertyType" ? (
                  <BiSolidUpArrow />
                ) : (
                  <BiSolidDownArrow />
                )}
              </span>

              {filters.propertyType.length > 0 && (
                <span className="cfd-selected-count">
                  {filters.propertyType.length}
                </span>
              )}
            </div>

            {activeFilter === "propertyType" && (
              <div className="cfd-filter-options">
                {propertyTypes.map((type) => (
                  <label key={type.label} className="cfd-custom-checkbox">
                    <input
                      type="checkbox"
                      checked={filters.propertyType.includes(type.value)}
                      onChange={() => {
                        handleCheckboxChange("propertyType", type.value);
                        trackButtonClick({
                          eventName: "open_cfd_all_list_sort",
                          eventCategory: "CFD",
                          eventSource: "on sort click",
                          eventLabel: "Property Type" - type.value,
                        });
                      }}
                    />
                    <span className="cfd-checkmark"></span>
                    {type.label}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Occupancy Filter */}
          <div
            className="cfd-filter-box"
            onMouseLeave={() => setActiveFilter(null)}
            onMouseEnter={() => toggleFilter("occupancy")}
          >
            <div className="cfd-filter-header">
              <div className="cfd-filter-header-content">
                {/* <BiHome className="cfd-filter-icon" /> */}
                <span>Occupancy</span>
              </div>

              <span className="mx-1">
                {activeFilter === "occupancy" ? (
                  <BiSolidUpArrow />
                ) : (
                  <BiSolidDownArrow />
                )}
              </span>

              {filters.occupancy.length > 0 && (
                <span className="cfd-selected-count">
                  {filters.occupancy.length}
                </span>
              )}
            </div>

            {activeFilter === "occupancy" && (
              <div className="cfd-filter-options">
                {occupancyStatuses.map((status) => (
                  <label key={status.label} className="cfd-custom-checkbox">
                    <input
                      type="checkbox"
                      checked={filters.occupancy.includes(status.value)}
                      onChange={() => {
                        handleCheckboxChange("occupancy", status.value);
                        trackButtonClick({
                          eventName: "open_cfd_all_list_sort",
                          eventCategory: "CFD",
                          eventSource: "on sort click",
                          eventLabel: "Occupancy" - status.value,
                        });
                      }}
                    />
                    <span className="cfd-checkmark"></span>
                    {status.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Time Frame Filter */}
      <div
        className="cfd-filter-box"
        onMouseEnter={() => toggleFilter("timeFrame")}
        onMouseLeave={() => setActiveFilter(null)}
      >
        <div className="cfd-filter-header">
          <div className="cfd-filter-header-content">
            {/* <BiSort className="cfd-filter-icon" /> */}
            <span>
              {filters.timeFrame
                ? sortBy.find((item) => item.value === filters.timeFrame)?.label
                : activeTab !== "history"
                ? "New Properties"
                : "Offers Placed"}
            </span>
          </div>

          <span className="mx-1">
            {activeFilter === "timeFrame" ? (
              <BiSolidUpArrow />
            ) : (
              <BiSolidDownArrow />
            )}
          </span>

          {/* {filters.timeFrame && <span className="selected-count">1</span>} */}
        </div>
        {activeFilter === "timeFrame" && (
          <div className="cfd-filter-options">
            {sortBy.map((timeFrame) => (
              <div
                key={timeFrame.value}
                className={`cfd-filter-option ${
                  filters.timeFrame === timeFrame.label ? "selected" : ""
                }`}
                onClick={() => {
                  onFilterChange("timeFrame", timeFrame.value, activeTab);
                  setActiveFilter(null);
                  trackButtonClick({
                    eventName: "open_cfd_all_list_sort",
                    eventCategory: "CFD",
                    eventSource: "on sort click",
                    eventLabel: timeFrame.label,
                  });
                  trackButtonClick({
                    eventName: "cfd_all_listings_tab_sort",
                    eventCategory: "CFD",
                    eventSource: "on sort click",
                    eventLabel: timeFrame.label,
                  });
                }}
              >
                {timeFrame.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reset Filters Button */}
      {activeTab !== "history" && (
        <div
          className="cfd-reset-box"
          onClick={() => {
            onFilterChange("propertyType", []);
            onFilterChange("occupancy", []);
            onFilterChange("timeFrame", "");
            setActiveFilter(null);

            trackButtonClick({
              eventName: "cfd_all_list_reset_filters_click",
              eventCategory: "CFD dashboard",
              eventSource: "on reset filters click",
              eventLabel: `Section: CFD | Filters reset: propertyType, occupancy, timeFrame`,
            });
          }}
          onMouseEnter={(e) => {
            e.currentTarget.setAttribute("data-tooltip-visible", "true");
          }}
          onMouseLeave={(e) => {
            e.currentTarget.setAttribute("data-tooltip-visible", "false");
          }}
          data-tooltip-visible="false"
        >
          <BiRotateRight className="cfd-reset-icon" />
          <span className="cfd-tooltip">Reset all filters</span>
        </div>
      )}
    </div>
  );
};

export default CfdPropertyFilters;
